import Dexie from 'dexie';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { CacheNames } from 'constants/enums';
import { captureException } from 'utils/captureException';

export type Job = {
  id: string;
  downloaded: boolean;
  createdAt: number;
  updatedAt: number;
};

Dexie.debug = 'dexie';

export const indexedDB = new Dexie(CacheNames.apiDB);
indexedDB.version(1).stores({
  [CacheNames.jobs]: 'id,createdAt,updatedAt',
});

export const saveToIndexedDB = async (
  storeName: string,
  data: unknown,
): Promise<void> => {
  try {
    await indexedDB.table(storeName).put(data);
  } catch (error) {
    captureException(error);
  }
};

export const removeTableFromIndexedDB = async (
  storeName: string,
): Promise<void> => {
  try {
    await indexedDB.table(storeName).clear();
  } catch (error) {
    captureException(error);
  }
};

export const removeByIdFromIndexedDB = async (
  storeName: string,
  id: string,
): Promise<void> => {
  if (id) {
    try {
      await indexedDB.table(storeName).delete(id);
    } catch (error) {
      captureException(error);
    }
  }
};

export const removeIndexedDB = async (): Promise<void> => {
  try {
    indexedDB.close();
    await Dexie.delete(CacheNames.apiDB);
  } catch (error) {
    captureException(error);
  }
};

export async function getAllJobsFromDB(): Promise<Job[]> {
  console.log('✊ getAllJobsFromDB');
  try {
    const store = indexedDB.table<Job>(CacheNames.jobs);
    return await store.orderBy('createdAt').reverse().toArray();
  } catch (error) {
    captureException(error);
    return [];
  }
}

export const setJobsToDB = async (jobs: Job[]): Promise<void> => {
  console.log('✊ setJobsToDB');
  try {
    const table = indexedDB.table<Job>(CacheNames.jobs);
    await Dexie.Promise.all(jobs.map((job) => table.put(job)));
    jobEventBus.emit(EventBusEvents.JobsUpdated);
  } catch (error) {
    captureException(error);
  }
};

export const clearAllJobsFromIndexedDB = async (): Promise<void> => {
  console.log('✊ clearAllJobsFromIndexedDB');
  try {
    const table = indexedDB.table<Job>(CacheNames.jobs);
    await table.clear();
    jobEventBus.emit(EventBusEvents.JobsUpdated);
  } catch (error) {
    captureException(error);
  }
};

export async function setOrReplaceJobInDB(
  jid: string,
  downloaded = false,
): Promise<void> {
  console.log('✊ setOrReplaceJobInDB', jid);
  try {
    const now = Date.now();
    const jobs = await getAllJobsFromDB();

    let found = false;
    const updated = jobs.map((job) => {
      if (job.id === jid) {
        found = true;
        return { ...job, downloaded, updatedAt: now };
      }
      return job;
    });

    if (!found) {
      updated.unshift({
        id: jid,
        downloaded,
        createdAt: now,
        updatedAt: now,
      });
    }

    await setJobsToDB(updated);
  } catch (error) {
    captureException(error);
    throw error;
  }
}

export async function removeJobFromDB(jid: string): Promise<void> {
  console.log('✊ removeJobFromDB');
  try {
    await indexedDB.table<Job>(CacheNames.jobs).delete(jid);
    jobEventBus.emit(EventBusEvents.JobsUpdated);
  } catch (error) {
    captureException(error);
  }
}
